import { useState, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

export interface ConnectionUser {
  id: number;
  first_name: string;
  last_name: string;
  age?: number;
  city?: string;
  profile_photo_url?: string;
}

export interface Connection {
  id: number;
  user_id: number;
  target_user_id: number;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'BLOCKED';
  created_at: string;
  updated_at: string;
  connected_user?: ConnectionUser;
  target_user?: ConnectionUser;
}

export interface ConnectionStatus {
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'BLOCKED' | null;
  exists: boolean;
}

export const useConnections = () => {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConnections = useCallback(async (page = 1, limit = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/contacts', { params: { page, limit } });
      setConnections(response.data.items || []);
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to fetch connections';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendConnectionRequest = useCallback(async (targetUserId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/contacts/request', { target_user_id: targetUserId });
      toast.success('Connected successfully. Contact details unlocked.');
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to send request';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const blockUser = useCallback(async (userId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post(`/contacts/${userId}/block`, {});
      toast.success('User blocked');
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to block user';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const unblockUser = useCallback(async (userId: number) => {
    setLoading(true);
    setError(null);
    try {
      await api.post(`/contacts/${userId}/unblock`, {});
      toast.success('User unblocked');
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to unblock user';
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const getConnectionStatus = useCallback(async (userId: number): Promise<ConnectionStatus | null> => {
    try {
      const response = await api.get(`/contacts/${userId}/status`);
      return response.data;
    } catch (err: any) {
      console.error('Failed to fetch connection status:', err);
      return null;
    }
  }, []);

  return {
    connections,
    loading,
    error,
    fetchConnections,
    sendConnectionRequest,
    blockUser,
    unblockUser,
    getConnectionStatus,
  };
};
